/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */

import React, { useRef, useState, useEffect } from "react";

import "@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css";
import "@ta-interaktiv/semantic-ui/semantic/dist/components/site.css";
import "@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css";
import "@ta-interaktiv/semantic-ui/semantic/dist/components/container.css";
import "@ta-interaktiv/semantic-ui/semantic/dist/components/header.css";
import "@ta-interaktiv/semantic-ui/semantic/dist/components/image.css";
import "@ta-interaktiv/semantic-ui/semantic/dist/components/list.css";
import "@ta-interaktiv/semantic-ui/semantic/dist/components/loader.css";
import "@ta-interaktiv/semantic-ui/semantic/dist/components/dimmer.css";

import "./styles.scss";

import htmlParser from "html-react-parser";
import parseSheet from "lib/parseSheet";

import { Waypoint } from "react-waypoint";

import { Masthead } from "@ta-interaktiv/react-masthead";
import { Footer } from "@ta-interaktiv/react-footer";
import { ThemeSwitch } from "@ta-interaktiv/react-theme-switch";
import { Tracking } from "lib/Tracking";

import Loader from "./loader";
import Cover from "../cover";
import Memorial from "../memorial";
import LeafletMap from "../leaflet-map";

// import { cssColors as colors } from "lib/colors";
import { sections } from "lib/memorials";

// TODO Set the correct article ID here
const ARTICLE_ID = "30977955";

const sheetId =
  "https://spreadsheets.google.com/feeds/list/" +
  "1XN0bj92OvsRBZIMDhwC5MjiPg9avyfZpTPQaz-9NXVU" +
  "/1/public/values?alt=json";

export function AppComponent() {
  // initial content
  const [activeSections, setActiveSections] = useState(null);
  const [content, setContent] = useState(null);

  // layout
  const refContainer = useRef(null);
  const [isMobile, updateIsMobile] = useState(null);
  const [mapHeight, updateHeight] = useState(0);

  // scrollytelling
  const [section, updateSection] = useState("most");
  const [item, updateItem] = useState("all");
  const [place, updatePlace] = useState(null);

  // deep linking
  const [hash, updateHash] = useState(null);

  // "componentDidMount-effect"
  useEffect(() => {
    updateIsMobile(window.innerWidth < 800);

    fetch(sheetId)
      .then(res => res.json())
      .then(json => {
        return parseSheet(json);
      })
      .then(data => {
        return data;
      })
      .then(data => {
        setContent(data.columns);
        setActiveSections(data.columns.sectionactive);
      });
  }, []);

  // deep linking hack
  useEffect(() => {
    const s = Object.values(sections);
    const addressbarHash = window.location.hash.replace("#", "");

    if (addressbarHash) {
      if (!s.includes(addressbarHash)) {
        return;
      }

      if (content && activeSections && hash === null) {
        updateHash(addressbarHash);
        updateSection(addressbarHash);

        const node = document.getElementById(addressbarHash);

        if (node) {
          node.scrollIntoView();
        }
      }
    }
  });

  useEffect(() => {
    // wait for data to arrive or else maps won't be rendered
    // and we can't get container heights...
    if (activeSections) {
      const {
        height
      } = refContainer.current.refs.leafletMap.container.getBoundingClientRect();

      updateHeight(height);
    }
  });

  // waypoint callback for items
  function itemHandler(name, position, props, firstOrLast) {
    if (
      position === "top" &&
      props.currentPosition === "above" &&
      props.previousPosition === "inside"
    ) {
      updateItem(name);
    }
    if (
      position === "top" &&
      !!firstOrLast === true &&
      props.previousPosition === "above" &&
      props.currentPosition === "inside"
    ) {
      updateItem("all");
    }
    if (
      position === "bottom" &&
      props.previousPosition === "above" &&
      props.currentPosition === "inside"
    ) {
      updateItem(name);
    }

    if (
      position === "bottom" &&
      !!firstOrLast === true &&
      props.previousPosition === "inside" &&
      props.currentPosition === "above"
    ) {
      updateItem("all");
    }
  }

  // waypoint callback for sections
  function sectionHandler(name, position, props) {
    if (
      position === "top" &&
      props.previousPosition === "below" &&
      props.currentPosition === "inside"
    ) {
      updateSection(name);
    }
    if (
      position === "bottom" &&
      props.previousPosition === "above" &&
      props.currentPosition === "inside"
    ) {
      updateSection(name);
    }
  }

  return (
    <div className="index">
      <Masthead articleId={ARTICLE_ID} />
      <ThemeSwitch />

      <header className="ui vertical very relaxed segment">
        <Cover
          title="Die versteckten Geschichten von Berns Denkmälern"
          subtitle=""
        />
      </header>
      <Tracking eventAction="vertical-scroll" eventLabel="startReading" />
      <div className="ui vertical segment">
        <div className="ui text container">
          <p>
            Sie wurden verehrt und vermehrt, umplatziert und zurückgestutzt:
            Berns Denkmäler haben eine spannende Vergangenheit. Für einmal
            gingen wir deshalb nicht achtlos an ihnen vorbei, sondern verweilten
            und vertieften uns in ihre Geschichte und Geschichten, in ihre
            Gemeinsamkeiten und Besonderheiten.
          </p>
          <p>
            Aus den vielen Werken, die auf Plätzen und in Parkanlagen an
            Personen oder Ereignisse erinnern, haben wir einige ausgewählt, neu
            gruppiert und in einer Serie zum Jahreswechsel 2019/2020
            porträtiert.
          </p>

          <p>
            Im Folgenden finden Sie diese Serie im Überblick – und damit auch
            die ganze Familie der Berner Denkmäler: Die Umgezogenen etwa, die
            Modernen oder diejenigen, welche die Welt nach Bern bringen.
          </p>

          <p>
            Die Antworten zu den spannendsten Fragen, die sich zu allen
            Denkmälern stellen, – wie{" "}
            <i>«Wem gehören die Denkmäler überhaupt?»</i>,{" "}
            <i>«Wird es je wieder ein neues grosses Denkmal in Bern geben?»</i>{" "}
            oder: <i>«Darf man an den Denkmälern herumklettern?»</i> –{" "}
            <a href="https://www.derbund.ch/18791266">
              <i className="external alternate icon" />
              finden Sie in unserem «FAQ» an dieser Stelle
            </a>
            .
          </p>
          <p>
            Und dass hinter einem Denkmal immer zwei Aussagen stecken und wie
            sich die Erinnerungsstätten im Lauf der Zeit verändert haben,
            erklärt der Historiker André Holenstein{" "}
            <a href={content && content["interview-link"][0]}>
              <i className="external alternate icon" />
              im Interview.
            </a>
          </p>
        </div>

        {activeSections === null ? (
          // LOADER
          <Loader />
        ) : (
          // CONTENT
          <div className="ui text container">
            {/*

            SECTION MEISTAUFGESTELLTE

          */}
            {activeSections[0] === "ja" && (
              <div className="item" id="most">
                <Tracking eventAction="vertical-scroll" eventLabel="readMost" />
                <Waypoint
                  fireOnRapidScroll
                  onEnter={props => sectionHandler("most", "top", props)}
                  onLeave={props => sectionHandler("most", "top", props)}
                />
                <h2 className="item-title dividing ui infographic header">
                  Einmal ist nicht genug
                </h2>
                <p>{content.meistaufgestellte[3]}</p>
                <div className="leaflet-top-container">
                  <LeafletMap
                    ref={refContainer}
                    currentItems={item}
                    currentSection={section}
                    currentPlace={place}
                    updatePlace={updatePlace}
                  />
                  <Waypoint
                    fireOnRapidScroll
                    onEnter={props =>
                      itemHandler("haller", "top", props, "first")
                    }
                    onLeave={props =>
                      itemHandler("haller", "top", props, "first")
                    }
                    topOffset={mapHeight + "px"}
                  >
                    <span>{/* <hr /> */}</span>
                  </Waypoint>
                  <Memorial
                    name="haller"
                    content={content}
                    updatePlace={updatePlace}
                    currentPlace={place}
                    isMobile={isMobile}
                    currentItem={item}
                  />
                  <Waypoint
                    fireOnRapidScroll
                    onEnter={props => itemHandler("haller", "bottom", props)}
                    onLeave={props => itemHandler("haller", "bottom", props)}
                    topOffset={mapHeight + "px"}
                  >
                    <span>{/* <hr /> */}</span>
                  </Waypoint>
                  <Waypoint
                    fireOnRapidScroll
                    onEnter={props => itemHandler("kocher", "top", props)}
                    onLeave={props => itemHandler("kocher", "top", props)}
                    topOffset={mapHeight + "px"}
                  >
                    <span>{/* <hr /> */}</span>
                  </Waypoint>
                  <Memorial
                    name="kocher"
                    content={content}
                    updatePlace={updatePlace}
                    currentPlace={place}
                    isMobile={isMobile}
                    currentItem={item}
                    floatRight
                  />
                  <Waypoint
                    fireOnRapidScroll
                    onEnter={props => itemHandler("kocher", "bottom", props)}
                    onLeave={props => itemHandler("kocher", "bottom", props)}
                    topOffset={mapHeight + "px"}
                  >
                    <span>{/* <hr /> */}</span>
                  </Waypoint>
                  {/* <Waypoint
                    fireOnRapidScroll
                    onEnter={props => itemHandler("bubenberg", "top", props)}
                    onLeave={props => itemHandler("bubenberg", "top", props)}
                    topOffset={mapHeight + "px"}
                  >
                    <span>{/* <hr /> </span>
                  </Waypoint>
                  <Memorial
                    name="bubenberg"
                    content={content}
                    updatePlace={updatePlace}
                    currentPlace={place}
                    isMobile={isMobile}
                    currentItem={item}
                    color={colors.THIRD}
                  />
                  <Waypoint
                    fireOnRapidScroll
                    onEnter={props =>
                      itemHandler("bubenberg", "bottom", props, "last")
                    }
                    onLeave={props =>
                      itemHandler("bubenberg", "bottom", props, "last")
                    }
                    topOffset={mapHeight + "px"}
                  >
                    <span>{/* <hr /></span>
                  </Waypoint> */}
                </div>
                {htmlParser(content.meistaufgestellte[0])}
                <div className="ui icon message">
                  <i className="newspaper outline icon" />
                  <div className="content">
                    <div className="header">
                      Zum weiterführenden Text auf der «Bund»-Website:
                    </div>
                    <p>
                      <i className="external alternate icon" />
                      <a href={content.meistaufgestellte[2]}>
                        «{content.meistaufgestellte[1]}»
                      </a>
                    </p>
                  </div>
                </div>
                <Waypoint
                  fireOnRapidScroll
                  onEnter={props => sectionHandler("most", "bottom", props)}
                  onLeave={props => sectionHandler("most", "bottom", props)}
                  topOffset={mapHeight * 1.5}
                />
              </div>
            )}
            {/*

            SECTION UMGEZOGENE

          */}
            {activeSections[1] === "ja" && (
              <div className="item" id="moved">
                <Tracking
                  eventAction="vertical-scroll"
                  eventLabel="readMoved"
                />
                <Waypoint
                  fireOnRapidScroll
                  onEnter={props => sectionHandler("moved", "top", props)}
                  onLeave={props => sectionHandler("moved", "top", props)}
                />
                <h2 className="dividing ui infographic header">
                  Die Umgezogenen
                </h2>
                <p>{content.umgezogene[3]}</p>
                <div className="leaflet-top-container">
                  <LeafletMap
                    ref={refContainer}
                    currentItems={item}
                    currentSection={section}
                    currentPlace={place}
                    updatePlace={updatePlace}
                  />
                  <Waypoint
                    fireOnRapidScroll
                    onEnter={props =>
                      itemHandler("erlach", "top", props, "first")
                    }
                    onLeave={props =>
                      itemHandler("erlach", "top", props, "first")
                    }
                    topOffset={mapHeight + "px"}
                  >
                    <span>{/* <hr /> */}</span>
                  </Waypoint>
                  <Memorial
                    name="erlach"
                    content={content}
                    updatePlace={updatePlace}
                    currentPlace={place}
                    isMobile={isMobile}
                    currentItem={item}
                  />
                  <Waypoint
                    fireOnRapidScroll
                    onEnter={props => itemHandler("erlach", "bottom", props)}
                    onLeave={props => itemHandler("erlach", "bottom", props)}
                    topOffset={mapHeight + "px"}
                  >
                    <span>{/* <hr /> */}</span>
                  </Waypoint>
                  <Waypoint
                    fireOnRapidScroll
                    onEnter={props => itemHandler("zähringer", "top", props)}
                    onLeave={props => itemHandler("zähringer", "top", props)}
                    topOffset={mapHeight + "px"}
                  >
                    <span>{/* <hr /> */}</span>
                  </Waypoint>
                  <Memorial
                    name="zähringer"
                    content={content}
                    updatePlace={updatePlace}
                    currentPlace={place}
                    isMobile={isMobile}
                    currentItem={item}
                    floatRight
                  />
                  <Waypoint
                    fireOnRapidScroll
                    onEnter={props => itemHandler("zähringer", "bottom", props)}
                    onLeave={props => itemHandler("zähringer", "bottom", props)}
                    topOffset={mapHeight + "px"}
                  >
                    <span>{/* <hr /> */}</span>
                  </Waypoint>
                  {/*
                  <Waypoint
                    fireOnRapidScroll
                    onEnter={props => itemHandler("gotthelf", "top", props)}
                    onLeave={props => itemHandler("gotthelf", "top", props)}
                    topOffset={mapHeight + "px"}
                  >
                    <span>{<hr />}</span>
                  </Waypoint>
                  <Memorial
                    name="gotthelf"
                    content={content}
                    updatePlace={updatePlace}
                    currentPlace={place}
                    isMobile={isMobile}
                    currentItem={item}
                  />
                  <Waypoint
                    fireOnRapidScroll
                    onEnter={props => itemHandler("gotthelf", "bottom", props)}
                    onLeave={props => itemHandler("gotthelf", "bottom", props)}
                    topOffset={mapHeight + "px"}
                  >
                    <span>{ <hr />}</span>
                  </Waypoint>
                  */}
                  <Waypoint
                    fireOnRapidScroll
                    onEnter={props => itemHandler("bubenberg", "top", props)}
                    onLeave={props => itemHandler("bubenberg", "top", props)}
                    topOffset={mapHeight + "px"}
                  >
                    <span>{/* <hr /> */}</span>
                  </Waypoint>
                  <Memorial
                    name="bubenberg"
                    content={content}
                    updatePlace={updatePlace}
                    currentPlace={place}
                    isMobile={isMobile}
                    currentItem={item}
                  />
                  <Waypoint
                    fireOnRapidScroll
                    onEnter={props =>
                      itemHandler("bubenberg", "bottom", props, "last")
                    }
                    onLeave={props =>
                      itemHandler("bubenberg", "bottom", props, "last")
                    }
                    topOffset={mapHeight + "px"}
                  >
                    <span>{/* <hr /> */}</span>
                  </Waypoint>
                </div>
                {htmlParser(content.umgezogene[0])}
                <div className="ui icon message">
                  <i className="newspaper outline icon" />
                  <div className="content">
                    <div className="header">
                      Zum weiterführenden Text auf der «Bund»-Website:
                    </div>
                    <p>
                      <i className="external alternate icon" />
                      <a href={content.umgezogene[2]}>
                        «{content.umgezogene[1]}»
                      </a>
                    </p>
                  </div>
                </div>
                <Waypoint
                  fireOnRapidScroll
                  onEnter={props => sectionHandler("moved", "bottom", props)}
                  onLeave={props => sectionHandler("moved", "bottom", props)}
                  topOffset={mapHeight * 1.5}
                />
              </div>
            )}
            {/*

            SECTION KOPFLOSE

          */}
            {activeSections[2] === "ja" && (
              <div className="item" id="headless">
                <Tracking
                  eventAction="vertical-scroll"
                  eventLabel="readHeadless"
                />
                <Waypoint
                  fireOnRapidScroll
                  onEnter={props => {
                    sectionHandler("headless", "top", props);
                  }}
                  onLeave={props => sectionHandler("headless", "top", props)}
                />
                <h2 className="dividing ui infographic header">Die Kuriosen</h2>
                <p>{content.kopflose[3]}</p>
                <div className="leaflet-top-container">
                  <LeafletMap
                    ref={refContainer}
                    currentItems={item}
                    currentSection={section}
                    currentPlace={place}
                    updatePlace={updatePlace}
                  />
                  <Waypoint
                    fireOnRapidScroll
                    onEnter={props =>
                      itemHandler("weinzäpfli", "top", props, "first")
                    }
                    onLeave={props =>
                      itemHandler("weinzäpfli", "top", props, "first")
                    }
                    topOffset={mapHeight + "px"}
                  >
                    <span>{/* <hr /> */}</span>
                  </Waypoint>
                  <Memorial
                    name="weinzäpfli"
                    content={content}
                    updatePlace={updatePlace}
                    currentPlace={place}
                    isMobile={isMobile}
                    currentItem={item}
                  />
                  <Waypoint
                    fireOnRapidScroll
                    onEnter={props =>
                      itemHandler("weinzäpfli", "bottom", props)
                    }
                    onLeave={props =>
                      itemHandler("weinzäpfli", "bottom", props)
                    }
                    topOffset={mapHeight + "px"}
                  >
                    <span>{/* <hr /> */}</span>
                  </Waypoint>
                  {/*
                  <Waypoint
                    fireOnRapidScroll
                    onEnter={props => itemHandler("wehrmänner", "top", props)}
                    onLeave={props => itemHandler("wehrmänner", "top", props)}
                    topOffset={mapHeight + "px"}
                  >
                    <span>{}</span>
                  </Waypoint>
                  <Memorial
                    name="wehrmänner"
                    content={content}
                    updatePlace={updatePlace}
                    currentPlace={place}
                    isMobile={isMobile}
                    currentItem={item}
                    floatRight
                  />
                  <Waypoint
                    fireOnRapidScroll
                    onEnter={props =>
                      itemHandler("wehrmänner", "bottom", props)
                    }
                    onLeave={props =>
                      itemHandler("wehrmänner", "bottom", props)
                    }
                    topOffset={mapHeight + "px"}
                  >
                    <span>{}</span>
                  </Waypoint> */}
                  <Waypoint
                    fireOnRapidScroll
                    onEnter={props =>
                      itemHandler("aargauerstalden", "top", props)
                    }
                    onLeave={props =>
                      itemHandler("aargauerstalden", "top", props)
                    }
                    topOffset={mapHeight + "px"}
                  >
                    <span>{/* <hr /> */}</span>
                  </Waypoint>
                  <Memorial
                    name="aargauerstalden"
                    content={content}
                    updatePlace={updatePlace}
                    currentPlace={place}
                    isMobile={isMobile}
                    currentItem={item}
                    floatRight
                  />
                  <Waypoint
                    fireOnRapidScroll
                    onEnter={props =>
                      itemHandler("aargauerstalden", "bottom", props)
                    }
                    onLeave={props =>
                      itemHandler("aargauerstalden", "bottom", props)
                    }
                    topOffset={mapHeight + "px"}
                  >
                    <span>{/* <hr /> */}</span>
                  </Waypoint>
                  <Waypoint
                    fireOnRapidScroll
                    onEnter={props => itemHandler("vonwerdt", "top", props)}
                    onLeave={props => itemHandler("vonwerdt", "top", props)}
                    topOffset={mapHeight + "px"}
                  >
                    <span>{/* <hr /> */}</span>
                  </Waypoint>
                  <Memorial
                    name="vonwerdt"
                    content={content}
                    updatePlace={updatePlace}
                    currentPlace={place}
                    isMobile={isMobile}
                    currentItem={item}
                  />
                  <Waypoint
                    fireOnRapidScroll
                    onEnter={props =>
                      itemHandler("vonwerdt", "bottom", props, "last")
                    }
                    onLeave={props =>
                      itemHandler("vonwerdt", "bottom", props, "last")
                    }
                    topOffset={mapHeight + "px"}
                  >
                    <span>{/* <hr /> */}</span>
                  </Waypoint>
                </div>
                {htmlParser(content.kopflose[0])}
                <div className="ui icon message">
                  <i className="newspaper outline icon" />
                  <div className="content">
                    <div className="header">
                      Zum weiterführenden Text auf der «Bund»-Website:
                    </div>
                    <p>
                      <i className="external alternate icon" />
                      <a href={content.kopflose[2]}>«{content.kopflose[1]}»</a>
                    </p>
                  </div>
                </div>
                <Waypoint
                  fireOnRapidScroll
                  onEnter={props => sectionHandler("headless", "bottom", props)}
                  onLeave={props => sectionHandler("headless", "bottom", props)}
                  topOffset={mapHeight * 1.5}
                />
              </div>
            )}
            {/*

            SECTION NATÜRLICHE

          */}
            {activeSections[3] === "ja" && (
              <div className="item" id="natural">
                <Tracking
                  eventAction="vertical-scroll"
                  eventLabel="readNatural"
                />
                <Waypoint
                  fireOnRapidScroll
                  onEnter={props => sectionHandler("natural", "top", props)}
                  onLeave={props => sectionHandler("natural", "top", props)}
                />
                <h2 className="dividing ui infographic header">
                  Ein wachsendes Denkmal
                </h2>
                <p>{content.natürliche[3]}</p>
                <div className="leaflet-top-container">
                  <LeafletMap
                    ref={refContainer}
                    currentItems={item}
                    currentSection={section}
                    currentPlace={place}
                    updatePlace={updatePlace}
                  />
                  <Waypoint
                    fireOnRapidScroll
                    onEnter={props =>
                      itemHandler("kopp", "top", props, "first")
                    }
                    onLeave={props =>
                      itemHandler("kopp", "top", props, "first")
                    }
                    topOffset={mapHeight + "px"}
                  >
                    <span>{/* <hr /> */}</span>
                  </Waypoint>
                  <Memorial
                    name="kopp"
                    content={content}
                    updatePlace={updatePlace}
                    currentPlace={place}
                    isMobile={isMobile}
                    currentItem={item}
                  />
                  <Waypoint
                    fireOnRapidScroll
                    onEnter={props =>
                      itemHandler("kopp", "bottom", props, "last")
                    }
                    onLeave={props =>
                      itemHandler("kopp", "bottom", props, "last")
                    }
                    topOffset={mapHeight + "px"}
                  >
                    <span>{/* <hr /> */}</span>
                  </Waypoint>
                  {/*
                  <Waypoint
                    fireOnRapidScroll
                    onEnter={props => itemHandler("kuenzi", "top", props)}
                    onLeave={props => itemHandler("kuenzi", "top", props)}
                    topOffset={mapHeight + "px"}
                  >
                    <span>{}</span>
                  </Waypoint>
                  <Memorial
                    name="kuenzi"
                    content={content}
                    updatePlace={updatePlace}
                    currentPlace={place}
                    isMobile={isMobile}
                    currentItem={item}
                    floatRight
                  />
                  <Waypoint
                    fireOnRapidScroll
                    onEnter={props => itemHandler("kuenzi", "bottom", props)}
                    onLeave={props => itemHandler("kuenzi", "bottom", props)}
                    topOffset={mapHeight + "px"}
                  >
                    <span>{}</span>
                  </Waypoint>
                  <Waypoint
                    fireOnRapidScroll
                    onEnter={props => itemHandler("studer", "top", props)}
                    onLeave={props => itemHandler("studer", "top", props)}
                    topOffset={mapHeight + "px"}
                  >
                    <span>{}</span>
                  </Waypoint>
                  <Memorial
                    name="studer"
                    content={content}
                    updatePlace={updatePlace}
                    currentPlace={place}
                    isMobile={isMobile}
                    currentItem={item}
                  />
                  <Waypoint
                    fireOnRapidScroll
                    onEnter={props =>
                      itemHandler("studer", "bottom", props, "last")
                    }
                    onLeave={props =>
                      itemHandler("studer", "bottom", props, "last")
                    }
                    topOffset={mapHeight + "px"}
                  >
                    <span>{}</span>
                  </Waypoint>
                  */}
                </div>
                {htmlParser(content.natürliche[0])}
                <div className="ui icon message">
                  <i className="newspaper outline icon" />
                  <div className="content">
                    <div className="header">
                      Zum weiterführenden Text auf der «Bund»-Website:
                    </div>
                    <p>
                      <i className="external alternate icon" />
                      <a href={content.natürliche[2]}>
                        «{content.natürliche[1]}»
                      </a>
                    </p>
                  </div>
                </div>
                <Waypoint
                  fireOnRapidScroll
                  onEnter={props => sectionHandler("natural", "bottom", props)}
                  onLeave={props => sectionHandler("natural", "bottom", props)}
                  topOffset={mapHeight * 1.5}
                />
              </div>
            )}
            {/*

            SECTION INTERNATIONAL

          */}
            {activeSections[4] === "ja" && (
              <div className="item" id="world">
                <Tracking
                  eventAction="vertical-scroll"
                  eventLabel="readWorld"
                />
                <Waypoint
                  fireOnRapidScroll
                  onEnter={props => sectionHandler("world", "top", props)}
                  onLeave={props => sectionHandler("world", "top", props)}
                />
                <h2 className="dividing ui infographic header">
                  Die Internationalen
                </h2>
                <p>{content.internationale[3]}</p>
                <div className="leaflet-top-container">
                  <LeafletMap
                    ref={refContainer}
                    currentItems={item}
                    currentSection={section}
                    currentPlace={place}
                    updatePlace={updatePlace}
                  />
                  <Waypoint
                    fireOnRapidScroll
                    onEnter={props =>
                      itemHandler("rizal", "top", props, "first")
                    }
                    onLeave={props =>
                      itemHandler("rizal", "top", props, "first")
                    }
                    topOffset={mapHeight + "px"}
                  >
                    <span>{/* <hr /> */}</span>
                  </Waypoint>
                  <Memorial
                    name="rizal"
                    content={content}
                    updatePlace={updatePlace}
                    currentPlace={place}
                    isMobile={isMobile}
                    currentItem={item}
                  />
                  <Waypoint
                    fireOnRapidScroll
                    onEnter={props =>
                      itemHandler("rizal", "bottom", props, "first")
                    }
                    onLeave={props =>
                      itemHandler("rizal", "bottom", props, "first")
                    }
                    topOffset={mapHeight + "px"}
                  >
                    <span>{/* <hr /> */}</span>
                  </Waypoint>
                  <Waypoint
                    fireOnRapidScroll
                    onEnter={props => itemHandler("bolivar", "top", props)}
                    onLeave={props => itemHandler("bolivar", "top", props)}
                    topOffset={mapHeight + "px"}
                  >
                    <span>{/* <hr /> */}</span>
                  </Waypoint>
                  <Memorial
                    name="bolivar"
                    content={content}
                    updatePlace={updatePlace}
                    currentPlace={place}
                    isMobile={isMobile}
                    currentItem={item}
                    floatRight
                  />
                  <Waypoint
                    fireOnRapidScroll
                    onEnter={props => itemHandler("bolivar", "bottom", props)}
                    onLeave={props => itemHandler("bolivar", "bottom", props)}
                    topOffset={mapHeight + "px"}
                  >
                    <span>{/* <hr /> */}</span>
                  </Waypoint>
                  {/*
                  <Waypoint
                    fireOnRapidScroll
                    onEnter={props => itemHandler("titulescu", "top", props)}
                    onLeave={props => itemHandler("titulescu", "top", props)}
                    topOffset={mapHeight + "px"}
                  >
                    <span>{}</span>
                  </Waypoint>
                  <Memorial
                    name="titulescu"
                    content={content}
                    updatePlace={updatePlace}
                    currentPlace={place}
                    isMobile={isMobile}
                    currentItem={item}
                  />
                  <Waypoint
                    fireOnRapidScroll
                    onEnter={props => itemHandler("titulescu", "bottom", props)}
                    onLeave={props => itemHandler("titulescu", "bottom", props)}
                    topOffset={mapHeight + "px"}
                  >
                    <span>{}</span>
                  </Waypoint>
                  */}
                  <Waypoint
                    fireOnRapidScroll
                    onEnter={props => itemHandler("mazzini", "top", props)}
                    onLeave={props => itemHandler("mazzini", "top", props)}
                    topOffset={mapHeight + "px"}
                  />
                  <Memorial
                    name="mazzini"
                    content={content}
                    updatePlace={updatePlace}
                    currentPlace={place}
                    isMobile={isMobile}
                    currentItem={item}
                  />
                  <Waypoint
                    fireOnRapidScroll
                    onEnter={props => itemHandler("mazzini", "bottom", props)}
                    onLeave={props => itemHandler("mazzini", "bottom", props)}
                    topOffset={mapHeight + "px"}
                  />

                  <Waypoint
                    fireOnRapidScroll
                    onEnter={props => itemHandler("lenin", "top", props)}
                    onLeave={props => itemHandler("lenin", "top", props)}
                    topOffset={mapHeight + "px"}
                  >
                    <span>{/* <hr /> */}</span>
                  </Waypoint>
                  <Memorial
                    name="lenin"
                    content={content}
                    updatePlace={updatePlace}
                    currentPlace={place}
                    isMobile={isMobile}
                    currentItem={item}
                    floatRight
                  />
                  <Waypoint
                    fireOnRapidScroll
                    onEnter={props => itemHandler("lenin", "bottom", props)}
                    onLeave={props => itemHandler("lenin", "bottom", props)}
                    topOffset={mapHeight + "px"}
                  >
                    <span>{/* <hr /> */}</span>
                  </Waypoint>
                  <Waypoint
                    fireOnRapidScroll
                    onEnter={props => itemHandler("refugianten", "top", props)}
                    onLeave={props => itemHandler("refugianten", "top", props)}
                    topOffset={mapHeight + "px"}
                  >
                    <span>{/* <hr /> */}</span>
                  </Waypoint>
                  <Memorial
                    name="refugianten"
                    content={content}
                    updatePlace={updatePlace}
                    currentPlace={place}
                    isMobile={isMobile}
                    currentItem={item}
                  />
                  <Waypoint
                    fireOnRapidScroll
                    onEnter={props =>
                      itemHandler("refugianten", "bottom", props, "last")
                    }
                    onLeave={props =>
                      itemHandler("refugianten", "bottom", props, "last")
                    }
                    topOffset={mapHeight + "px"}
                  >
                    <span>{/* <hr /> */}</span>
                  </Waypoint>
                </div>
                {htmlParser(content.internationale[0])}
                <div className="ui icon message">
                  <i className="newspaper outline icon" />
                  <div className="content">
                    <div className="header">
                      Zum weiterführenden Text auf der «Bund»-Website:
                    </div>
                    <p>
                      <i className="external alternate icon" />
                      <a href={content.internationale[2]}>
                        «{content.internationale[1]}»
                      </a>
                    </p>
                  </div>
                </div>
                <Waypoint
                  fireOnRapidScroll
                  onEnter={props => sectionHandler("world", "bottom", props)}
                  onLeave={props => sectionHandler("world", "bottom", props)}
                  topOffset={mapHeight * 1.5}
                />
              </div>
            )}
            {/*

            SECTION MODERNEN

          */}
            {activeSections[5] === "ja" && (
              <div className="item" id="modern">
                <Tracking
                  eventAction="vertical-scroll"
                  eventLabel="readModern"
                />
                <Waypoint
                  fireOnRapidScroll
                  onEnter={props => sectionHandler("modern", "top", props)}
                  onLeave={props => sectionHandler("modern", "top", props)}
                />
                <h2 className="dividing ui infographic header">Die Modernen</h2>
                <p>{content.moderne[3]}</p>
                <div className="leaflet-top-container">
                  <LeafletMap
                    ref={refContainer}
                    currentItems={item}
                    currentSection={section}
                    currentPlace={place}
                    updatePlace={updatePlace}
                  />
                  <Waypoint
                    fireOnRapidScroll
                    onEnter={props =>
                      itemHandler("korenhuisbrug", "top", props, "first")
                    }
                    onLeave={props =>
                      itemHandler("korenhuisbrug", "top", props, "first")
                    }
                    topOffset={mapHeight + "px"}
                  >
                    <span>{/* <hr /> */}</span>
                  </Waypoint>
                  <Memorial
                    name="korenhuisbrug"
                    content={content}
                    updatePlace={updatePlace}
                    currentPlace={place}
                    isMobile={isMobile}
                    currentItem={item}
                  />
                  <Waypoint
                    fireOnRapidScroll
                    onEnter={props =>
                      itemHandler("korenhuisbrug", "bottom", props)
                    }
                    onLeave={props =>
                      itemHandler("korenhuisbrug", "bottom", props)
                    }
                    topOffset={mapHeight + "px"}
                  >
                    <span>{/* <hr /> */}</span>
                  </Waypoint>
                  <Waypoint
                    fireOnRapidScroll
                    onEnter={props => itemHandler("einstein", "top", props)}
                    onLeave={props => itemHandler("einstein", "top", props)}
                    topOffset={mapHeight + "px"}
                  >
                    <span>{/* <hr /> */}</span>
                  </Waypoint>
                  <Memorial
                    name="einstein"
                    content={content}
                    updatePlace={updatePlace}
                    currentPlace={place}
                    isMobile={isMobile}
                    currentItem={item}
                    floatRight
                  />
                  <Waypoint
                    fireOnRapidScroll
                    onEnter={props => itemHandler("einstein", "bottom", props)}
                    onLeave={props => itemHandler("einstein", "bottom", props)}
                    topOffset={mapHeight + "px"}
                  >
                    <span>{/* <hr /> */}</span>
                  </Waypoint>
                  <Waypoint
                    fireOnRapidScroll
                    onEnter={props => itemHandler("dank", "top", props)}
                    onLeave={props => itemHandler("dank", "top", props)}
                    topOffset={mapHeight + "px"}
                  >
                    <span>{/* <hr /> */}</span>
                  </Waypoint>
                  <Memorial
                    name="dank"
                    content={content}
                    updatePlace={updatePlace}
                    currentPlace={place}
                    isMobile={isMobile}
                    currentItem={item}
                  />
                  <Waypoint
                    fireOnRapidScroll
                    onEnter={props => itemHandler("dank", "bottom", props)}
                    onLeave={props => itemHandler("dank", "bottom", props)}
                    topOffset={mapHeight + "px"}
                  >
                    <span>{/* <hr /> */}</span>
                  </Waypoint>
                  <Waypoint
                    fireOnRapidScroll
                    onEnter={props => itemHandler("stadionuhr", "top", props)}
                    onLeave={props => itemHandler("stadionuhr", "top", props)}
                    topOffset={mapHeight + "px"}
                  >
                    <span>{/* <hr /> */}</span>
                  </Waypoint>
                  <Memorial
                    name="stadionuhr"
                    content={content}
                    updatePlace={updatePlace}
                    currentPlace={place}
                    isMobile={isMobile}
                    currentItem={item}
                    floatRight
                  />
                  <Waypoint
                    fireOnRapidScroll
                    onEnter={props =>
                      itemHandler("stadionuhr", "bottom", props)
                    }
                    onLeave={props =>
                      itemHandler("stadionuhr", "bottom", props)
                    }
                    topOffset={mapHeight + "px"}
                  >
                    <span>{/* <hr /> */}</span>
                  </Waypoint>
                </div>
                {htmlParser(content.moderne[0])}
                <div className="ui icon message">
                  <i className="newspaper outline icon" />
                  <div className="content">
                    <div className="header">
                      Zum weiterführenden Text auf der «Bund»-Website:
                    </div>
                    <p>
                      <i className="external alternate icon" />
                      <a href={content.moderne[2]}>«{content.moderne[1]}»</a>
                    </p>
                  </div>
                </div>
                <Waypoint
                  fireOnRapidScroll
                  onEnter={props => sectionHandler("modern", "bottom", props)}
                  onLeave={props => sectionHandler("modern", "bottom", props)}
                  topOffset={mapHeight * 1.5}
                />
              </div>
            )}
            {/*

            SECTION Frauen

          */}
            {activeSections[6] === "ja" && (
              <div className="item" id="women">
                <Tracking
                  eventAction="vertical-scroll"
                  eventLabel="readFrauen"
                />
                <Waypoint
                  fireOnRapidScroll
                  onEnter={props => sectionHandler("women", "top", props)}
                  onLeave={props => sectionHandler("women", "top", props)}
                />
                <h2 className="dividing ui infographic header">Die Frauen</h2>
                <p>{content.frauen[3]}</p>
                <div className="leaflet-top-container">
                  <LeafletMap
                    ref={refContainer}
                    currentItems={item}
                    currentSection={section}
                    currentPlace={place}
                    updatePlace={updatePlace}
                  />
                  <Waypoint
                    fireOnRapidScroll
                    onEnter={props =>
                      itemHandler("weltpost", "top", props, "first")
                    }
                    onLeave={props =>
                      itemHandler("weltpost", "top", props, "first")
                    }
                    topOffset={mapHeight + "px"}
                  >
                    <span>{/* <hr /> */}</span>
                  </Waypoint>
                  <Memorial
                    name="weltpost"
                    content={content}
                    updatePlace={updatePlace}
                    currentPlace={place}
                    isMobile={isMobile}
                    currentItem={item}
                  />
                  <Waypoint
                    fireOnRapidScroll
                    onEnter={props => itemHandler("weltpost", "bottom", props)}
                    onLeave={props => itemHandler("weltpost", "bottom", props)}
                    topOffset={mapHeight + "px"}
                  >
                    <span>{/* <hr /> */}</span>
                  </Waypoint>
                  <Waypoint
                    fireOnRapidScroll
                    onEnter={props => itemHandler("telegraphen", "top", props)}
                    onLeave={props => itemHandler("telegraphen", "top", props)}
                    topOffset={mapHeight + "px"}
                  >
                    <span>{/* <hr /> */}</span>
                  </Waypoint>
                  <Memorial
                    name="telegraphen"
                    content={content}
                    updatePlace={updatePlace}
                    currentPlace={place}
                    isMobile={isMobile}
                    currentItem={item}
                    floatRight
                  />
                  <Waypoint
                    fireOnRapidScroll
                    onEnter={props =>
                      itemHandler("telegraphen", "bottom", props)
                    }
                    onLeave={props =>
                      itemHandler("telegraphen", "bottom", props)
                    }
                    topOffset={mapHeight + "px"}
                  >
                    <span>{/* <hr /> */}</span>
                  </Waypoint>
                  <Waypoint
                    fireOnRapidScroll
                    onEnter={props => itemHandler("minerva", "top", props)}
                    onLeave={props => itemHandler("minerva", "top", props)}
                    topOffset={mapHeight + "px"}
                  >
                    <span>{/* <hr /> */}</span>
                  </Waypoint>
                  <Memorial
                    name="minerva"
                    content={content}
                    updatePlace={updatePlace}
                    currentPlace={place}
                    isMobile={isMobile}
                    currentItem={item}
                  />
                  <Waypoint
                    fireOnRapidScroll
                    onEnter={props => itemHandler("minerva", "bottom", props)}
                    onLeave={props => itemHandler("minerva", "bottom", props)}
                    topOffset={mapHeight + "px"}
                  >
                    <span>{/* <hr /> */}</span>
                  </Waypoint>
                  <Waypoint
                    fireOnRapidScroll
                    onEnter={props => itemHandler("seiler", "top", props)}
                    onLeave={props => itemHandler("seiler", "top", props)}
                    topOffset={mapHeight + "px"}
                  >
                    <span>{/* <hr /> */}</span>
                  </Waypoint>
                  <Memorial
                    name="seiler"
                    content={content}
                    updatePlace={updatePlace}
                    currentPlace={place}
                    isMobile={isMobile}
                    currentItem={item}
                    floatRight
                  />
                  <Waypoint
                    fireOnRapidScroll
                    onEnter={props => itemHandler("seiler", "bottom", props)}
                    onLeave={props => itemHandler("seiler", "bottom", props)}
                    topOffset={mapHeight + "px"}
                  >
                    <span>{/* <hr /> */}</span>
                  </Waypoint>
                </div>
                {htmlParser(content.frauen[0])}
                <div className="ui icon message">
                  <i className="newspaper outline icon" />
                  <div className="content">
                    <div className="header">
                      Zum weiterführenden Text auf der «Bund»-Website:
                    </div>
                    <p>
                      <i className="external alternate icon" />
                      <a href={content.frauen[2]}>«{content.frauen[1]}»</a>
                    </p>
                  </div>
                </div>
                <Waypoint
                  fireOnRapidScroll
                  onEnter={props => sectionHandler("women", "bottom", props)}
                  onLeave={props => sectionHandler("women", "bottom", props)}
                  topOffset={mapHeight * 1.5}
                />
              </div>
            )}
          </div>
        )}
      </div>

      <Footer
        articleId={ARTICLE_ID}
        hidePortfolioTeaser
        mailTo="bern@derbund.ch"
        credits={[
          ["Idee", "Silvia Staub"],
          ["Konzept", "Silvia Staub, Brigitte Walser"],
          [
            "Text",
            "Silvia Staub, Mathias Streit, Brigitte Walser, Simon Wälti, Calum MacKenzie, Céline Graf, Sophie Reinhardt"
          ],
          ["Foto", "Adrian Moser, Franziska Rothenbühler, Alexandra Jäggi"],
          ["Digitale Umsetzung", "Christian Zellweger"],
          ["Leitung Redaktion Bern", "Marcello Odermatt"]
        ]}
      />
    </div>
  );
}
